import React, { useState, useRef, useEffect, useMemo } from 'react';
import {
  Typography,
  TextField
} from '@mui/material';
import WidgetWrapper from './WidgetWrapper';

const TextLabel = ({
  text = 'Label',
  textAlign = 'left',
  tagId = null,
  tagValue = null, // Will be injected by preview mode
  value = null, // Support 'value' prop (from tagBinding.property)
  parentText = null, // Parent widget text (for ALARM widgets)
  unit = '', // Unit suffix (e.g., "°C", "kW", "m³/h")
  valueMap = null, // Value mapping: { "0": "OFF", "1": "ON" } - maps raw values to display text
  numberFormat = null, // Number formatting options: { decimals, decimalSeparator, thousandsSeparator }
  editable = false, // Enable inline editing in preview mode
  onEdit = null, // Callback when text is edited
  // Status label support (from selectbox conversion)
  isStatusLabel = false,
  originalOptions = null,
  // Style properties
  backgroundColor,
  foreColor,
  borderWidth,
  borderColor,
  borderRadius,
  fontSize,
  fontWeight,
  fontFamily,
  opacity,
  ...props
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editText, setEditText] = useState('');
  const inputRef = useRef(null);

  const justifyContent = textAlign === 'center' ? 'center' : textAlign === 'right' ? 'flex-end' : 'flex-start';

  // Helper to unwrap tag value objects
  const unwrap = (v) => {
    if (v && typeof v === 'object') {
      if (v.value !== undefined) return v.value;
      if (v.raw !== undefined) return v.raw;
    }
    return v;
  };

  // Get the actual tag value (prioritize value over tagValue), unwrap objects
  const rawValue = value !== null && value !== undefined ? value : tagValue;
  const actualValue = unwrap(rawValue);

  // 🏷️ STATUS LABEL: Dynamic styles based on tag value and options
  const getStatusLabelStyles = useMemo(() => {
    if (!isStatusLabel || !originalOptions || !Array.isArray(originalOptions)) {
      return { text: null, backgroundColor: null, foreColor: null };
    }

    // Find matching option based on tag value
    if (actualValue !== null && actualValue !== undefined) {
      const valueStr = String(actualValue);
      const matchingOption = originalOptions.find(opt => String(opt.value) === valueStr);

      if (matchingOption) {
        return {
          text: matchingOption.label || null,
          backgroundColor: matchingOption.color || null,
          foreColor: matchingOption.textColor || null
        };
      }
    }

    // Default to first option if no match
    const defaultOption = originalOptions[0];
    return {
      text: defaultOption.label || null,
      backgroundColor: defaultOption.color || null,
      foreColor: defaultOption.textColor || null
    };
  }, [isStatusLabel, originalOptions, actualValue, tagId]);

  // Helper function to format numbers
  // Default format: Turkish style (dot as thousands, comma as decimal, 2 decimals)
  const formatNumber = (value, format) => {
    if (typeof value !== 'number') return value;

    let decimals = 2;
    let decimalSeparator = ',';
    let thousandsSeparator = '.';

    // Handle string format like "0.0", "0.00", "0"
    if (typeof format === 'string') {
      const match = format.match(/^0(\.0+)?$/);
      if (match) {
        decimals = match[1] ? match[1].length - 1 : 0;
      }
    } else if (typeof format === 'object' && format !== null) {
      // Handle object format { decimals, decimalSeparator, thousandsSeparator }
      decimals = format.decimals ?? 2;
      decimalSeparator = format.decimalSeparator || ',';
      thousandsSeparator = format.thousandsSeparator || '.';
    }
    // If format is null/undefined, use defaults (Turkish format)

    // Round to specified decimals
    const rounded = Number(value.toFixed(decimals));

    // Split into integer and decimal parts
    const [intPart, decPart] = rounded.toFixed(decimals).split('.');

    // Add thousands separator to integer part
    const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

    // Combine with decimal part using specified separator
    return decPart ? `${formattedInt}${decimalSeparator}${decPart}` : formattedInt;
  };

  // Enhanced display text logic for better binding support
  const displayText = useMemo(() => {
    // 🏷️ STATUS LABEL: Use dynamic text from options
    if (isStatusLabel) {
      if (getStatusLabelStyles.text) {
        return getStatusLabelStyles.text;
      }
    }

    // Check if this is an ALARM widget and handle 0/1 to NORMAL/ALARM mapping
    const widgetTypeText = (parentText || text || '').toString().toUpperCase();
    const isAlarmWidget = widgetTypeText === 'ALARM' || widgetTypeText === 'ARIZA';

    // Priority order:
    // 1. If valueMap is defined and we have a value, use mapped text
    // 2. If tagValue/value is set for ALARM widget, map 0/1 to NORMAL/ALARM
    // 3. If tagValue is set, use it (direct tag binding) + unit if available
    // 4. If text is set and not the default, use it (property binding or static text)
    // 5. Fall back to empty string (not 'Label')

    // 🗺️ VALUE MAP: Check if we have a valueMap and a valid value to map
    if (valueMap && actualValue !== null && actualValue !== undefined) {
      const valueKey = String(actualValue).trim();
      // Try exact match first, then numeric string match
      if (valueMap[valueKey] !== undefined) {
        return valueMap[valueKey];
      }
      // Try parsing as number and matching
      const numericKey = String(Math.round(parseFloat(actualValue)));
      if (valueMap[numericKey] !== undefined) {
        return valueMap[numericKey];
      }
    }

    if (isAlarmWidget && actualValue !== null && actualValue !== undefined) {
      const numericValue = parseFloat(actualValue);
      if (numericValue === 0 || String(actualValue).toUpperCase().trim() === '0') {
        return 'NORMAL';
      } else if (numericValue === 1 || String(actualValue).toUpperCase().trim() === '1') {
        return 'ALARM';
      }
    }

    if (tagValue !== null && tagValue !== undefined) {
      // Apply number formatting for numeric values (always use default Turkish format if not specified)
      const numValue = Number(tagValue);
      const isNumeric = !isNaN(numValue) && tagValue !== '' && tagValue !== true && tagValue !== false;
      const formattedValue = isNumeric ? formatNumber(numValue, numberFormat) : String(tagValue);
      // Add unit if available (and unit is not null/undefined/empty)
      return (unit && unit !== 'None') ? `${formattedValue} ${unit}` : formattedValue;
    }

    // Allow empty string - don't treat it as falsy
    if (text !== undefined && text !== null && text !== 'Label' && text !== '') {
      return String(text);
    }

    // Return empty string instead of 'Label'
    return '';
  }, [tagValue, value, actualValue, text, unit, numberFormat, parentText, isStatusLabel, getStatusLabelStyles, valueMap]);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleDoubleClick = () => {
    if (editable && !tagId) { // Don't allow editing if bound to a tag
      setEditText(displayText);
      setIsEditing(true);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleSave = () => {
    if (onEdit && editText.trim() !== '') {
      onEdit(editText);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditText('');
  };

  const handleBlur = () => {
    handleSave();
  };

  // Context menu handler - dispatches global event
  const handleContextMenu = (event) => {
    if (tagId) {
      event.preventDefault();
      event.stopPropagation();

      // Dispatch global event to open Tag Info Modal
      const displayName = (text && text !== 'Label') ? text : `Tag ${tagId}`;
      const tagInfoData = {
        tagId: tagId,
        tagName: displayName,
        tagName2: '',
        currentValue: actualValue,
        unit: unit || ''
      };
      window.dispatchEvent(new CustomEvent('openTagInfo', { detail: tagInfoData }));
    }
  };

  // Dynamic backgroundColor for ALARM/NORMAL widgets
  const getDynamicBackgroundColor = () => {
    // 🏷️ STATUS LABEL: Highest priority - use dynamic background if available
    if (isStatusLabel && getStatusLabelStyles.backgroundColor) {
      return getStatusLabelStyles.backgroundColor;
    }

    // Check if this is an ALARM widget by:
    // 1. Static backgroundColor is red (indicates ALARM widget type)
    // 2. OR text/parentText matches ALARM/ARIZA/NORMAL
    const isRedBackground = backgroundColor && (
      backgroundColor.toLowerCase() === '#ff2400' ||
      backgroundColor.toLowerCase().includes('rgb(255') ||
      backgroundColor.toLowerCase() === 'red'
    );

    const widgetTypeText = (parentText || text || displayText || '').toString().toUpperCase();
    const isAlarmWidget =
      isRedBackground ||
      widgetTypeText === 'ALARM' ||
      widgetTypeText === 'ARIZA' ||
      widgetTypeText === 'NORMAL';

    // If this is an ALARM-type widget and has a tag value, determine color based on value
    if (isAlarmWidget && actualValue !== null && actualValue !== undefined) {
      const numericValue = parseFloat(actualValue);
      const stringValue = String(actualValue).toUpperCase().trim();

      // 0 = NORMAL = white, 1 = ALARM = red
      if (numericValue === 0 || stringValue === 'NORMAL' || stringValue === '0') {
        return '#ffffff';
      } else if (numericValue === 1 || stringValue === 'ALARM' || stringValue === 'ARIZA' || stringValue === '1') {
        return '#ff2400';
      }
    }

    // Default: use the static backgroundColor
    return backgroundColor;
  };

  const finalBackgroundColor = getDynamicBackgroundColor();

  // 🏷️ STATUS LABEL: Override with dynamic foreColor if available
  const finalForeColor = isStatusLabel && getStatusLabelStyles.foreColor
    ? getStatusLabelStyles.foreColor
    : foreColor;

  // 🏷️ STATUS LABEL: Apply border styling for status labels
  const finalBorderWidth = isStatusLabel ? (borderWidth || 2) : borderWidth;
  const finalBorderColor = isStatusLabel ? (borderColor || '#000000') : borderColor;
  const finalBorderRadius = isStatusLabel ? (borderRadius || 4) : borderRadius;

  return (
    <WidgetWrapper
      backgroundColor={finalBackgroundColor}
      foreColor={finalForeColor}
      borderWidth={finalBorderWidth}
      borderColor={finalBorderColor}
      borderRadius={finalBorderRadius}
      fontSize={fontSize}
      fontWeight={fontWeight}
      fontFamily={fontFamily}
      opacity={opacity}
      justifyContent={justifyContent}
      padding="4px"
      {...props}
    >
      {isEditing ? (
        <TextField
          ref={inputRef}
          value={editText}
          onChange={(e) => setEditText(e.target.value)}
          onKeyPress={handleKeyPress}
          onBlur={handleBlur}
          variant="outlined"
          size="small"
          fullWidth
          sx={{
            '& .MuiOutlinedInput-root': {
              fontSize: 'inherit',
              fontWeight: 'inherit',
              color: 'inherit',
              fontFamily: 'inherit',
              padding: 0,
              '& fieldset': {
                border: '1px solid #2196f3'
              }
            },
            '& .MuiOutlinedInput-input': {
              padding: '2px 4px',
              textAlign
            }
          }}
        />
      ) : (
        <Typography
          style={{
            fontSize: 'inherit',
            fontWeight: 'inherit',
            color: 'inherit',
            fontFamily: 'inherit',
            textAlign,
            wordBreak: 'break-word',
            width: '100%',
            cursor: editable && !tagId ? 'pointer' : (tagId ? 'context-menu' : 'default')
          }}
          onDoubleClick={handleDoubleClick}
          onContextMenu={handleContextMenu}
          title={editable && !tagId ? 'Double-click to edit' : (tagId ? 'Right-click for tag options' : undefined)}
        >
          {displayText}
        </Typography>
      )}
    </WidgetWrapper>
  );
};

export default TextLabel;